import { useState, useEffect } from "react";
import styles from "./template.style";
import api from "../api";
import { useNavigate } from "react-router-dom";

export default function TemplateCreator() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [subCategory, setSubCategory] = useState("DEFAULT");
  const [language, setLanguage] = useState("en_US");
  const [mediaType, setMediaType] = useState("None");
  const [headerText, setHeaderText] = useState("");
  const [body, setBody] = useState("");
  const [footer, setFooter] = useState("");
  const [previewurl, setPreviewUrl] = useState("");
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaHandle, setMediaHandle] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [variableSamples, setVariableSamples] = useState({});
  const [errors, setErrors] = useState({});

  // Parse variables from body and update samples
  useEffect(() => {
    const regex = /{{(\d+)}}/g;
    let match;
    const found = [];
    while ((match = regex.exec(body)) !== null) {
      found.push(match[1]);
    }

    setVariableSamples(prev => {
      const next = { ...prev };
      found.forEach(v => {
        if (!next[v]) next[v] = "";
      });
      // Remove samples that are no longer in the body
      Object.keys(next).forEach(v => {
        if (!found.includes(v)) delete next[v];
      });
      return next;
    });

    if (errors.body || errors.variableSamples) {
      setErrors(prev => ({ ...prev, body: "", variableSamples: "" }));
    }
  }, [body]);

  const validate = () => {
    const newErrors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = "Template name is required";
    } else if (!/^[a-z0-9_]+$/.test(name)) {
      newErrors.name = "Name can only contain lowercase letters, numbers, and underscores";
    }

    // Body validation
    if (!body.trim()) {
      newErrors.body = "Body content is required";
    } else {
      const regex = /{{(\d+)}}/g;
      const vars = [];
      let match;
      while ((match = regex.exec(body)) !== null) {
        vars.push(parseInt(match[1]));
      }

      if (vars.length > 0) {
        // Check for sequential variables starting from 1
        vars.sort((a, b) => a - b);
        const uniqueVars = [...new Set(vars)];
        for (let i = 0; i < uniqueVars.length; i++) {
          if (uniqueVars[i] !== i + 1) {
            newErrors.body = "Variables must be sequential starting from {{1}} (e.g. {{1}}, {{2}}...)";
            break;
          }
        }

        // Check if all variables have samples
        const missingSamples = uniqueVars.filter(v => !variableSamples[v] || !variableSamples[v].trim());
        if (missingSamples.length > 0) {
          newErrors.variableSamples = `Please provide sample values for all variables: {{${missingSamples.join("}}, {{")}}}}`;
        }
      }
    }

    // Header validation
    if (mediaType !== "None" && !mediaHandle) {
      newErrors.media = `Please upload a sample ${mediaType.toLowerCase()} for the header`;
    }

    // Buttons validation
    if (buttons.length > 10) {
      newErrors.buttons = "Maximum 10 buttons allowed";
    } else {
      const urlButtons = buttons.filter(b => b.type === "VISIT_WEBSITE");
      const phoneButtons = buttons.filter(b => b.type === "CALL_PHONE");

      if (urlButtons.length > 2) newErrors.buttons = "Maximum 2 Website buttons allowed";
      if (phoneButtons.length > 1) newErrors.buttons = "Maximum 1 Phone Number button allowed";

      buttons.forEach((btn, idx) => {
        if (!btn.text.trim()) {
          newErrors[`btn_${idx}_text`] = "Button text is required";
        }
        if (btn.type === "VISIT_WEBSITE" && !btn.url.trim()) {
          newErrors[`btn_${idx}_url`] = "Website URL is required";
        }
        if (btn.type === "CALL_PHONE" && !btn.phone.trim()) {
          newErrors[`btn_${idx}_phone`] = "Phone number is required";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMediaPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, media: "" }));

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/templates/upload-template-media",
        formData,
      );

      if (response.data.success) {
        setMediaHandle(response.data.mediaHandle);
        setPreviewUrl(response.data.url);
      } else {
        alert("Upload failed");
      }
    } catch (error) {
      console.error(error);
      alert("Error uploading media");
    }
  };

  const addVariable = () => {
    const regex = /{{(\d+)}}/g;
    let max = 0;
    let match;
    while ((match = regex.exec(body)) !== null) {
      const num = parseInt(match[1]);
      if (num > max) max = num;
    }
    setBody(prev => prev + ` {{${max + 1}}}`);
  };

  const getPreviewBody = () => {
    let text = body;
    Object.keys(variableSamples).forEach(v => {
      const sample = variableSamples[v] || `{{${v}}}`;
      text = text.replaceAll(`{{${v}}}`, sample);
    });
    return text || "Your message preview will appear here...";
  };

  const addButton = () => {
    if (buttons.length >= 10) return;
    setButtons([
      ...buttons,
      {
        type: "VISIT_WEBSITE",
        text: "",
        url: "",
        phone: "",
        countryCode: "+971",
      },
    ]);
  };

  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index][field] = value;
    setButtons(updated);
    if (errors[`btn_${index}_${field}`] || errors.buttons) {
      setErrors(prev => ({ ...prev, [`btn_${index}_${field}`]: "", buttons: "" }));
    }
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`btn_${index}_text`];
      delete next[`btn_${index}_url`];
      delete next[`btn_${index}_phone`];
      return next;
    });
  };

  const handleSaveTemplate = async () => {
    if (!validate()) {
      return;
    }
    setIsSaving(true);

    try {
      // Reformat buttons for backend to match Meta API requirements
      const normalizedButtons = buttons
        .filter((b) => b.text)
        .map((b) => {
          if (b.type === "VISIT_WEBSITE") {
            return {
              type: "URL",
              text: b.text,
              url: b.url,
            };
          }
          if (b.type === "COPY_OFFER_CODE") {
            // COPY_CODE button type
            return {
              type: "COPY_CODE",
              example: b.url || "PROMO123" // Meta expects the code in the example array or similar depending on API version
            };
          }
          if (b.type === "CALL_PHONE") {
            const fullPhone = (b.countryCode || "").replace(/\+/g, '') + (b.phone || "");
            return {
              type: "PHONE_NUMBER",
              text: b.text,
              phone_number: fullPhone, // Correct key is phone_number (snake_case)
            };
          }
          // Default for CUSTOM, CALL_WHATSAPP or others
          return {
            type: "QUICK_REPLY",
            text: b.text,
          };
        });

      // Prepare template object for backend validation
      let header = null;
      if (mediaType !== "None") {
        header = {
          format: mediaType,
          example: {
            header_handle: mediaHandle ? [mediaHandle] : [],
          },
        };
      } else if (headerText) {
        header = {
          format: "TEXT",
          text: headerText,
        };
      }

      const template = {
        name,
        category,
        language,
        body,
        header,
        footer,
        buttons: normalizedButtons.length > 0 ? normalizedButtons : [],
        previewUrl: previewurl || "",
      };

      const response = await api.post("/templates/store-template", {
        template,
      });

      if (response.data.success) {
        alert("Template submitted for approval!");
        navigate(-1);
      } else {
        alert(response.data.message || "Failed to save template");
      }
    } catch (error) {
      console.error(error);
      alert("Error saving template");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* LEFT SIDE: FORM */}
      <div style={styles.left}>
        {/* SECTION 0: SET UP YOUR TEMPLATE */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Set up your template</h3>
          <p style={styles.helper}>Choose the category that best describes your message template. Then select the type of message you want to send.</p>

          <div style={styles.tabsContainer}>
            <div
              style={{ ...styles.tab, ...(category === "MARKETING" ? styles.activeTab : {}) }}
              onClick={() => setCategory("MARKETING")}
            >
              📢 Marketing
            </div>
            <div
              style={{ ...styles.tab, ...(category === "UTILITY" ? styles.activeTab : {}) }}
              onClick={() => {
                setCategory("UTILITY");
                setMediaType("None"); // Utility templates usually dont have media headers
                setMediaPreview(null);
                setMediaHandle(null);
              }}
            >
              🛠️ Utility
            </div>
          </div>

          <div
            style={{ ...styles.subOption, ...(subCategory === "DEFAULT" ? styles.activeSubOption : {}) }}
            onClick={() => setSubCategory("DEFAULT")}
          >
            <div style={{ ...styles.radioCircle, ...(subCategory === "DEFAULT" ? styles.radioCircleActive : {}) }}>
              {subCategory === "DEFAULT" && <div style={styles.radioDot} />}
            </div>
            <div style={styles.subOptionContent}>
              <div style={styles.subOptionTitle}>Default</div>
              <div style={styles.subOptionDesc}>Send messages with media and customized buttons to engage your customers.</div>
            </div>
          </div>
        </div>

        {/* SECTION 1: NAME AND LANGUAGE */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Template name and language</h3>
          <div style={styles.grid}>
            <div>
              <label style={styles.label}>Name your template</label>
              <input
                style={{ ...styles.input, borderColor: errors.name ? '#ef4444' : '#d1d5db' }}
                placeholder="Enter a template name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value.toLowerCase().replace(/\s+/g, "_"));
                  if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
                }}
              />
              {errors.name && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{errors.name}</div>}
              <div style={styles.helper}>{name.length}/512</div>
            </div>
            <div>
              <label style={styles.label}>Select language</label>
              <select
                style={styles.input}
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="en">English</option>
                <option value="en_GB">English (UK)</option>
                <option value="en_US">English (US)</option>
              </select>
            </div>
          </div>
        </div>

        {/* SECTION 2: CONTENT */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Content</h3>
          <p style={styles.helper}>Add a header, body and footer for your template. Cloud API hosted by Meta will review the template variables and content.</p>

          <div style={styles.grid}>
            <div>
              <label style={styles.label}>Type of variable</label>
              <select style={styles.input} disabled>
                <option>Number</option>
              </select>
            </div>
            <div>
              <label style={styles.label}>Media sample • Optional</label>
              <select
                style={styles.input}
                value={mediaType}
                onChange={(e) => {
                  setMediaType(e.target.value);
                  setMediaPreview(null);
                  setMediaHandle(null);
                  setHeaderText("");
                  setErrors(prev => ({ ...prev, media: "" }));
                }}
              >
                <option value="None">None</option>
                {category === "MARKETING" && (
                  <>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="DOCUMENT">Document</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {mediaType === "None" ? (
            <div>
              <label style={styles.label}>Header • Optional</label>
              <input
                style={styles.input}
                placeholder="Add a short line of text to the header"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                maxLength={60}
              />
              <div style={styles.helper}>{headerText.length}/60</div>
            </div>
          ) : (
            <div>
              <label style={styles.label}>Upload {mediaType}</label>
              <input
                type="file"
                accept={
                  mediaType === "IMAGE" ? "image/*" :
                    mediaType === "VIDEO" ? "video/*" : ".pdf"
                }
                onChange={handleFileChange}
                style={{ borderColor: errors.media ? '#ef4444' : '#d1d5db' }}
              />
              {errors.media && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{errors.media}</div>}
            </div>
          )}

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={styles.label}>Body</label>
              <button style={styles.secondaryButton} onClick={addVariable}>+ Add variable</button>
            </div>
            <textarea
              style={{ ...styles.textarea, borderColor: errors.body ? '#ef4444' : '#d1d5db' }}
              placeholder="Enter message body..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={1024}
            />
            {errors.body && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{errors.body}</div>}
            <div style={styles.helper}>{body.length}/1024</div>
          </div>

          {/* VARIABLE SAMPLES */}
          {Object.keys(variableSamples).length > 0 && (
            <div style={styles.sampleGrid}>
              <label style={styles.label}>Variable Samples</label>
              <p style={styles.helper}>Include samples of all variables in your message to help Meta review your template.</p>
              {errors.variableSamples && <div style={{ color: '#ef4444', fontSize: '11px', marginBottom: '8px' }}>{errors.variableSamples}</div>}
              {Object.keys(variableSamples).sort((a, b) => a - b).map(v => (
                <div key={v} style={styles.sampleRow}>
                  <div style={styles.variableBadge}>{"{{"}{v}{"}}"}</div>
                  <input
                    style={styles.input}
                    placeholder="Sample value"
                    value={variableSamples[v]}
                    onChange={(e) => {
                      setVariableSamples(prev => ({ ...prev, [v]: e.target.value }));
                      if (errors.variableSamples) setErrors(prev => ({ ...prev, variableSamples: "" }));
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          <div>
            <label style={styles.label}>Footer • Optional</label>
            <input
              style={styles.input}
              placeholder="Add a short line of text to the bottom"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              maxLength={60}
            />
            <div style={styles.helper}>{footer.length}/60</div>
          </div>
        </div>

        {/* SECTION 3: BUTTONS */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Buttons • Optional</h3>
          <p style={styles.helper}>Create buttons that let customers respond to your message or take action.</p>
          {errors.buttons && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '12px', fontWeight: '500' }}>{errors.buttons}</div>}

          {buttons.map((btn, index) => (
            <div key={index} style={{ border: "1px solid #e5e7eb", padding: "20px", borderRadius: "8px", marginBottom: "16px", backgroundColor: '#fdfdfd' }}>
              <div style={styles.grid}>
                <div>
                  <label style={styles.label}>Type of Action</label>
                  <select
                    style={styles.input}
                    value={btn.type}
                    onChange={(e) => updateButton(index, "type", e.target.value)}
                  >
                    <option value="VISIT_WEBSITE">Visit website</option>
                    <option value="CALL_WHATSAPP">Call on WhatsApp</option>
                    <option value="CALL_PHONE">Call phone</option>
                    <option value="COPY_OFFER_CODE">Copy offer code</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Button Text</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...styles.input, paddingRight: '50px', borderColor: errors[`btn_${index}_text`] ? '#ef4444' : '#d1d5db' }}
                      placeholder="Button text"
                      value={btn.text}
                      onChange={(e) => updateButton(index, "text", e.target.value)}
                      maxLength={25}
                    />
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '11px',
                      color: '#9ca3af'
                    }}>
                      {btn.text.length}/25
                    </div>
                  </div>
                  {errors[`btn_${index}_text`] && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{errors[`btn_${index}_text`]}</div>}
                </div>
              </div>

              {btn.type === "CALL_WHATSAPP" && (
                <div style={{
                  marginTop: "16px",
                  padding: "16px",
                  background: "#f0f7ff",
                  borderRadius: "8px",
                  border: "1px solid #00a88422",
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start"
                }}>
                  <div style={{ color: "#00a884", fontSize: "16px", marginTop: '2px' }}>ℹ️</div>
                  <div style={{ fontSize: "13px", color: "#4b5563", lineHeight: "1.5" }}>
                    Turn on calling in the <a href="#" style={{ color: "#0084ff", textDecoration: "none" }}>WhatsApp Manager portal</a>. Alternatively, you can use the Phone Number button type.
                    <div style={{ marginTop: '4px' }}>
                      <a href="#" style={{ color: "#0084ff", textDecoration: "none", fontWeight: '500' }}>About calling on WhatsApp</a>
                    </div>
                  </div>
                </div>
              )}

              {btn.type === "CALL_PHONE" && (
                <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px" }}>
                  <div>
                    <label style={styles.label}>Country</label>
                    <select
                      style={styles.input}
                      value={btn.countryCode}
                      onChange={(e) => updateButton(index, "countryCode", e.target.value)}
                    >
                      <option value="+971">AE +971</option>
                      <option value="+91">IN +91</option>
                      <option value="+1">US +1</option>
                      <option value="+44">UK +44</option>
                      <option value="+966">SA +966</option>
                      <option value="+968">OM +968</option>
                      <option value="+974">QA +974</option>
                      <option value="+973">BH +973</option>
                      <option value="+965">KW +965</option>
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Phone number</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        style={{ ...styles.input, paddingRight: '50px', borderColor: errors[`btn_${index}_phone`] ? '#ef4444' : '#d1d5db' }}
                        placeholder="0/20"
                        value={btn.phone}
                        onChange={(e) => updateButton(index, "phone", e.target.value.replace(/\D/g, ''))}
                        maxLength={20}
                      />
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '11px',
                        color: '#9ca3af'
                      }}>
                        {btn.phone?.length || 0}/20
                      </div>
                    </div>
                    {errors[`btn_${index}_phone`] && (
                      <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                        {errors[`btn_${index}_phone`]}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(btn.type === "VISIT_WEBSITE" || btn.type === "COPY_OFFER_CODE") && (
                <div style={{ marginTop: "16px" }}>
                  <label style={styles.label}>{btn.type === "VISIT_WEBSITE" ? "Website URL" : "Offer Code"}</label>
                  <input
                    style={{ ...styles.input, borderColor: errors[`btn_${index}_url`] ? '#ef4444' : '#d1d5db' }}
                    placeholder={btn.type === "VISIT_WEBSITE" ? "https://example.com" : "CODE2024"}
                    value={btn.url}
                    onChange={(e) => updateButton(index, "url", e.target.value)}
                  />
                  {errors[`btn_${index}_url`] && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{errors[`btn_${index}_url`]}</div>}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button
                  style={{ ...styles.secondaryButton, color: "#ef4444", borderColor: "#ef4444" }}
                  onClick={() => removeButton(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          <button style={styles.secondaryButton} onClick={addButton} disabled={buttons.length >= 10}>
            + Add button
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <button style={{ ...styles.button, background: "#fff", color: "#374151", border: "1px solid #d1d5db" }} onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            style={styles.button}
            onClick={handleSaveTemplate}
            disabled={isSaving}
          >
            {isSaving ? "Submitting..." : "Submit for review"}
          </button>
        </div>
      </div>

      {/* RIGHT SIDE: PREVIEW */}
      <div style={styles.right}>
        <div style={styles.previewHeader}>Template preview</div>
        <div style={styles.previewContainer}>
          <div style={styles.messageBubble}>
            {mediaType !== "None" && (
              <div style={styles.previewMedia}>
                {mediaPreview ? (
                  mediaType === "IMAGE" ? (
                    <img src={mediaPreview} alt="p" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }} />
                  ) : mediaType === "VIDEO" ? (
                    <video src={mediaPreview} style={{ width: "100%", height: "100%", borderRadius: "6px" }} />
                  ) : (
                    <div style={{ textAlign: "center" }}>📄 Document</div>
                  )
                ) : (
                  <div style={{ color: "#999", fontSize: "14px" }}>{mediaType} PREVIEW</div>
                )}
              </div>
            )}

            {mediaType === "None" && headerText && (
              <div style={styles.previewHeaderText}>
                {headerText}
              </div>
            )}

            <div style={styles.previewBody}>{getPreviewBody()}</div>

            {footer && <div style={styles.previewFooter}>{footer}</div>}

            <div style={styles.previewTime}>10:42 AM</div>

            {buttons.length > 0 && (
              <div style={styles.previewButtons}>
                {buttons.map((btn, i) => (
                  <div key={i} style={styles.previewButton}>
                    {btn.text || "Button"}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
